import React from "react";
import { useEditor } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Square, Columns, Box, Grid3x3, Ruler, Move, Hand, Maximize2, Sun, Layers as LayersIcon, Magnet, ShieldAlert, ShieldCheck, ArrowLeftRight, Undo2, Redo2 } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MHE_PRESETS, findNarrowAisles, OBJECT_DEFS } from "@/lib/objectDefs";

export default function Toolbar() {
  const view = useEditor((s) => s.view);
  const setView = useEditor((s) => s.setView);
  const showGrid = useEditor((s) => s.showGrid);
  const showDimensions = useEditor((s) => s.showDimensions);
  const measureMode = useEditor((s) => s.measureMode);
  const toggleMeasure = useEditor((s) => s.toggleMeasure);
  const handMode = useEditor((s) => s.handMode);
  const toggleHand = useEditor((s) => s.toggleHand);
  const smartSnap = useEditor((s) => s.smartSnap);
  const toggleSmartSnap = useEditor((s) => s.toggleSmartSnap);
  const aisleGap = useEditor((s) => s.aisleGap);
  const setAisleGap = useEditor((s) => s.setAisleGap);
  const compliance = useEditor((s) => s.compliance);
  const setCompliance = useEditor((s) => s.setCompliance);
  const toggleCompliance = useEditor((s) => s.toggleCompliance);
  const showAisles = useEditor((s) => s.showAisles);
  const toggleShowAisles = useEditor((s) => s.toggleShowAisles);
  const objects = useEditor((s) => s.objects);
  const fitView = useEditor((s) => s.fitView);
  const undo = useEditor((s) => s.undo);
  const redo = useEditor((s) => s.redo);
  const canUndo = useEditor((s) => s.past.length > 0);
  const canRedo = useEditor((s) => s.future.length > 0);

  // Live-compute flagged aisles for the compliance summary badge.
  const flagged = compliance.enabled ? findNarrowAisles(objects, compliance.threshold) : [];
  const flaggedArea = flagged.reduce((s, f) => s + f.area_mm2, 0) / 1e6; // → m²
  // Racks analysed — use the same category filter as findNarrowAisles for a
  // consistent count (racks + super_racks categories only).
  const rackCount = React.useMemo(() => {
    return (objects || []).filter((o) => {
      const d = OBJECT_DEFS[o.type];
      return d && (d.category === "racks" || d.category === "super_racks");
    }).length;
  }, [objects]);

  return (
    <div className="h-10 border-b border-border/60 px-2 flex items-center gap-1.5 bg-card/60 shrink-0 min-w-0 overflow-x-auto flex-nowrap scrollbar-thin whitespace-nowrap [&>*]:shrink-0">
      <div className="flex items-center bg-background/40 border border-border/60 rounded-sm p-0.5 shrink-0">
        <ToolButton testId="view-2d" active={view === "2d"} onClick={() => setView("2d")} icon={<Square className="w-3.5 h-3.5" />} label="2D" />
        <ToolButton testId="view-split" active={view === "split"} onClick={() => setView("split")} icon={<Columns className="w-3.5 h-3.5" />} label="Split" />
        <ToolButton testId="view-3d" active={view === "3d"} onClick={() => setView("3d")} icon={<Box className="w-3.5 h-3.5" />} label="3D" />
      </div>

      {/* Undo / Redo — keyboard: Cmd/Ctrl+Z and Cmd/Ctrl+Shift+Z. */}
      <div className="flex items-center gap-0.5 shrink-0">
        <Button
          data-testid="undo-btn"
          size="icon"
          variant="ghost"
          disabled={!canUndo}
          onClick={undo}
          className="h-7 w-7 rounded-sm text-muted-foreground hover:text-foreground disabled:opacity-30"
          title="Undo (Ctrl/Cmd+Z)"
        >
          <Undo2 className="w-3.5 h-3.5" />
        </Button>
        <Button
          data-testid="redo-btn"
          size="icon"
          variant="ghost"
          disabled={!canRedo}
          onClick={redo}
          className="h-7 w-7 rounded-sm text-muted-foreground hover:text-foreground disabled:opacity-30"
          title="Redo (Ctrl/Cmd+Shift+Z)"
        >
          <Redo2 className="w-3.5 h-3.5" />
        </Button>
      </div>

      <div className="w-px h-5 bg-border/60 mx-1 shrink-0" />

      <Button
        data-testid="toggle-grid"
        size="sm"
        variant="ghost"
        className={`h-7 rounded-sm font-mono text-[10px] uppercase tracking-widest ${showGrid ? "text-foreground" : "text-muted-foreground"}`}
        onClick={() => useEditor.setState({ showGrid: !showGrid })}
      >
        <Grid3x3 className="w-3.5 h-3.5 mr-1" /> Grid
      </Button>
      <Button
        data-testid="toggle-dimensions"
        size="sm"
        variant="ghost"
        className={`h-7 rounded-sm font-mono text-[10px] uppercase tracking-widest ${showDimensions ? "text-foreground" : "text-muted-foreground"}`}
        onClick={() => useEditor.setState({ showDimensions: !showDimensions })}
      >
        <Ruler className="w-3.5 h-3.5 mr-1" /> Dim
      </Button>
      <Button
        data-testid="toggle-snap"
        size="sm"
        variant="ghost"
        className={`h-7 rounded-sm font-mono text-[10px] uppercase tracking-widest ${smartSnap ? "text-foreground" : "text-muted-foreground"}`}
        onClick={toggleSmartSnap}
        title="Snap edges flush to walls / other objects while dragging"
      >
        <Magnet className="w-3.5 h-3.5 mr-1" /> Snap
      </Button>
      {/* Aisle gap — desired distance between racks / walls for smart-snap.
          Always editable so users can type the target aisle even before
          enabling smart-snap. */}
      <div className={`flex items-center gap-1 h-7 rounded-sm px-1.5 border border-border/60 bg-background/40 shrink-0`}>
        <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">Aisle</span>
        <input
          data-testid="aisle-gap"
          type="number"
          min={0}
          step={100}
          value={aisleGap}
          onChange={(e) => setAisleGap(e.target.value)}
          onWheel={(e) => e.currentTarget.blur()}
          className="w-14 h-5 bg-transparent border-none outline-none text-center font-mono text-[11px] text-foreground focus:ring-0"
          title="Desired aisle width in mm. Smart-snap will offer this distance as a snap target."
        />
        <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">mm</span>
      </div>

      {/* Show aisles — persistent labelled dimension lines between parallel rack rows */}
      <Button
        data-testid="toggle-aisles"
        size="sm"
        variant="ghost"
        className={`h-7 rounded-sm font-mono text-[10px] uppercase tracking-widest ${showAisles ? "text-foreground" : "text-muted-foreground"}`}
        onClick={toggleShowAisles}
        title="Show / hide labelled aisle-width markers between rack rows"
      >
        <ArrowLeftRight className="w-3.5 h-3.5 mr-1" /> Aisles
      </Button>

      {/* Compliance check — flag aisles narrower than the MHE turning radius */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            data-testid="toggle-compliance"
            size="sm"
            variant="ghost"
            className={`h-7 rounded-sm font-mono text-[10px] uppercase tracking-widest gap-1 px-2 ${
              compliance.enabled
                ? flagged.length > 0
                  ? "text-destructive"
                  : "text-emerald-500"
                : "text-muted-foreground"
            }`}
            title="Compliance check — flag aisles narrower than MHE turning radius"
          >
            {compliance.enabled && flagged.length > 0
              ? <ShieldAlert className="w-3.5 h-3.5" />
              : <ShieldCheck className="w-3.5 h-3.5" />}
            <span>Compl</span>
            {compliance.enabled && flagged.length > 0 && (
              <span data-testid="compliance-badge" className="inline-flex h-4 min-w-4 px-1 items-center justify-center rounded-sm bg-destructive text-destructive-foreground font-mono text-[9px]">
                {flagged.length}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-72 p-3 rounded-sm">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">// compliance</div>
                <div className="text-sm font-semibold tracking-tight mt-0.5">Aisle Check</div>
              </div>
              <Button
                data-testid="compliance-enable"
                size="sm"
                variant={compliance.enabled ? "default" : "outline"}
                className="h-7 rounded-sm font-mono text-[10px] uppercase tracking-widest"
                onClick={toggleCompliance}
              >
                {compliance.enabled ? "On" : "Off"}
              </Button>
            </div>

            <div className="space-y-1.5">
              <label className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">MHE Preset</label>
              <Select
                value={compliance.preset}
                onValueChange={(v) => {
                  const preset = MHE_PRESETS[v] || MHE_PRESETS.custom;
                  setCompliance({ preset: v, threshold: preset.threshold });
                }}
              >
                <SelectTrigger data-testid="compliance-preset" className="h-8 text-xs rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(MHE_PRESETS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      {v.label} · {k === "custom" ? compliance.threshold : v.threshold}mm
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground flex justify-between">
                <span>Min Aisle Width</span>
                <span className="font-mono">mm</span>
              </label>
              <input
                data-testid="compliance-threshold"
                type="number"
                min={500}
                step={50}
                value={compliance.threshold}
                onChange={(e) => setCompliance({ preset: "custom", threshold: Math.max(500, Number(e.target.value) || 500) })}
                className="w-full h-8 rounded-sm border border-input bg-background px-2 font-mono text-xs"
              />
            </div>

            {/* Live summary */}
            <div className="border-t border-border/60 pt-2.5 space-y-1">
              <div className="flex justify-between font-mono text-[11px]">
                <span className="text-muted-foreground">Racks analysed</span>
                <span data-testid="compliance-rack-count">{rackCount}</span>
              </div>
              <div className="flex justify-between font-mono text-[11px]">
                <span className="text-muted-foreground">Flagged aisles</span>
                <span data-testid="compliance-count" className={flagged.length > 0 ? "text-destructive font-semibold" : "text-emerald-500 font-semibold"}>
                  {flagged.length}
                </span>
              </div>
              <div className="flex justify-between font-mono text-[11px]">
                <span className="text-muted-foreground">Impacted area</span>
                <span data-testid="compliance-area">{flaggedArea.toFixed(2)} m²</span>
              </div>
            </div>

            {flagged.length > 0 && (
              <div className="max-h-40 overflow-y-auto scrollbar-thin border border-destructive/30 rounded-sm bg-destructive/5">
                {flagged.map((f, i) => (
                  <div key={f.id} className="flex justify-between px-2 py-1 border-b last:border-0 border-destructive/20 font-mono text-[10px]">
                    <span className="text-muted-foreground">{i + 1}. Aisle ({f.axis.toUpperCase()})</span>
                    <span className="text-destructive font-semibold">{f.gap}mm</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      <div className="w-px h-5 bg-border/60 mx-1 shrink-0" />

      <Button
        data-testid="toggle-hand"
        size="sm"
        variant="ghost"
        className={`h-7 rounded-sm font-mono text-[10px] uppercase tracking-widest ${handMode ? "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground" : "text-muted-foreground"}`}
        onClick={toggleHand}
        title="Pan the canvas (H)"
      >
        <Hand className="w-3.5 h-3.5 mr-1" /> Hand
      </Button>
      <Button
        data-testid="toggle-measure"
        size="sm"
        variant="ghost"
        className={`h-7 rounded-sm font-mono text-[10px] uppercase tracking-widest ${measureMode ? "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground" : "text-muted-foreground"}`}
        onClick={toggleMeasure}
        title="Measuring tape (M)"
      >
        <Move className="w-3.5 h-3.5 mr-1" /> Tape
      </Button>
      <Button
        data-testid="fit-view"
        size="sm"
        variant="ghost"
        className="h-7 rounded-sm font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
        onClick={fitView}
        title="Fit to view (F)"
      >
        <Maximize2 className="w-3.5 h-3.5 mr-1" /> Fit
      </Button>

      <div className="w-px h-5 bg-border/60 mx-1 shrink-0" />

      {/* Environment presets */}
      <EnvSelect
        testId="sky-select"
        icon={<Sun className="w-3.5 h-3.5" />}
        selector={(s) => s.skyPreset}
        setter="setSkyPreset"
        options={[
          ["day", "Day"],
          ["sunset", "Sunset"],
          ["overcast", "Overcast"],
          ["night", "Night"],
        ]}
      />
      <EnvSelect
        testId="ground-select"
        icon={<LayersIcon className="w-3.5 h-3.5" />}
        selector={(s) => s.groundStyle}
        setter="setGroundStyle"
        options={[
          ["plain", "Plain"],
          ["grass", "Grass"],
          ["asphalt", "Asphalt"],
        ]}
      />

      <div className="flex-1" />
      {measureMode && <TapeReadout />}
      {handMode && <HandHint />}
      <PolygonHint />
    </div>
  );
}

function ToolButton({ active, onClick, icon, label, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`px-2.5 h-6 rounded-sm flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest transition-colors ${
        active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function PolygonHint() {
  const shape = useEditor((s) => s.floor.shape);
  const verts = useEditor((s) => s.floor.vertices);
  const measureMode = useEditor((s) => s.measureMode);
  const handMode = useEditor((s) => s.handMode);
  if (measureMode || handMode) return null;
  if (shape !== "polygon") return null;
  return (
    <div className="font-mono text-[10px] uppercase tracking-widest text-primary px-2">
      polygon mode · click to add point ({verts.length})
    </div>
  );
}

function HandHint() {
  return (
    <div className="font-mono text-[10px] uppercase tracking-widest text-primary px-2">
      hand · drag to pan · scroll = zoom · esc to exit
    </div>
  );
}

function EnvSelect({ testId, icon, selector, setter, options }) {
  const value = useEditor(selector);
  const set = useEditor((s) => s[setter]);
  return (
    <div className="flex items-center gap-1 text-muted-foreground">
      <span className="opacity-70">{icon}</span>
      <Select value={value} onValueChange={set}>
        <SelectTrigger
          data-testid={testId}
          className="h-7 rounded-sm bg-transparent border-border/60 font-mono text-[10px] uppercase tracking-widest px-2 min-w-[92px]"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map(([v, l]) => (
            <SelectItem key={v} value={v} className="font-mono text-xs">{l}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function TapeReadout() {
  const measure = useEditor((s) => s.measure);
  const cursorMm = useEditor((s) => s.cursorMm);
  // Live distance: if placing 2nd point, use cursor; if completed, use b.
  let dist = null;
  if (measure) {
    const end = measure.b || cursorMm;
    if (end) dist = Math.hypot(end.x - measure.a.x, end.y - measure.a.y);
  }
  return (
    <div className="flex items-center gap-2 pr-2">
      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        {measure ? (measure.b ? "click to start new · esc to exit" : "click end point · esc to cancel") : "click start point · esc to exit"}
      </span>
      {dist !== null && (
        <span
          data-testid="tape-readout"
          className="font-mono text-xs font-semibold bg-sky-500 text-white rounded-sm px-2 py-0.5 min-w-[86px] text-center tabular-nums"
        >
          {(dist / 1000).toFixed(2)} m
        </span>
      )}
    </div>
  );
}
